import AmountInput from 'components/AmountInput'
import CustomButton from 'components/Buttons/CustomButton'
import BreadCrumbs from 'components/breadcrumbs'
import { useAbilityAccess } from 'hooks/useAbilityAccess'
import { useEffect, useState } from 'react'
import { AlertCircle, Trash2, Eye } from 'react-feather'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { useIntl } from 'react-intl'
import {
  Card,
  Container,
  FormGroup,
  InputGroup,
  InputGroupText,
  Label,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Modal,
  ModalBody,
  Spinner
} from 'reactstrap'
import { TableComponent as DataTable } from 'components/TableComponent'
import { BUSINESS_TOPUP, MANAGE } from 'utils/subjectActions'
import { usePostOnePaymentMutation, useFetchPendingBcaAllQuery, useCancelBcaUniqueCodeByIdMutation, useFetchPendingOnePaymentQuery } from './topupApiSlice'

const TopupOnePayment = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const intl = useIntl()
  const auth = useSelector((state) => state.auth)
  const accessTopup = useAbilityAccess({
    action: MANAGE,
    subject: BUSINESS_TOPUP,
  })

  const [activeTab, setActiveTab] = useState(location.state?.activeTab || '1')
  const [amount, setAmount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [errMsg, setErrMsg] = useState('')
  const [postOnePayment] = usePostOnePaymentMutation()

  const { data: pendingBcaAllData, isFetching: loadingPending, refetch } = useFetchPendingBcaAllQuery(undefined, { skip: !auth?.token })
  const pendingBcaList = Array.isArray(pendingBcaAllData)
    ? pendingBcaAllData
    : pendingBcaAllData?.data || pendingBcaAllData?.items || []

  const { data: pendingOnePaymentData } = useFetchPendingOnePaymentQuery(undefined, { skip: !auth?.token })

  const [cancelBcaUniqueCodeById] = useCancelBcaUniqueCodeByIdMutation()
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)
  const [selectedCancelId, setSelectedCancelId] = useState(null)

  const handleCancelTopup = async (id) => {
    try {
      setLoading(true)
      await cancelBcaUniqueCodeById({ topupuniquecodeId: id }).unwrap()
      setIsCancelModalOpen(false)
      setSelectedCancelId(null)
      refetch()
    } catch (error) {
      // Error cancellation
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {
      name: 'Batas Waktu',
      selector: row => row.expiredDate,
      minWidth: '150px',
      cell: row => (
        <div>
          {row?.expiredDate ? new Date(row.expiredDate).toLocaleString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
        </div>
      )
    },
    {
      name: 'Metode',
      selector: row => row.bankCodeReceiver,
      minWidth: '150px',
      cell: row => (
        <div className="fw-bold text-dark">
          {row.bankCodeReceiver === 'BCA' ? 'Transfer BCA' : `Transfer ${row.bankCodeReceiver}`}
        </div>
      )
    },
    {
      name: 'Jumlah',
      selector: row => row.amountPlusUniqueCode,
      minWidth: '150px',
      cell: row => (
        <div className="fw-bold text-dark">
          Rp{intl.formatNumber(Number(row?.amountPlusUniqueCode || row?.amount || 0), { useGrouping: 'always' })}
        </div>
      )
    },
    {
      name: 'Aksi',
      minWidth: '150px',
      cell: row => (
        <div className="d-flex gap-2">
          <CustomButton
            size="sm"
            onClick={() => {
              const isExpired = row.expiredDate ? new Date() > new Date(row.expiredDate) : false;
              if (!isExpired) {
                if (row?.one_payment_token) {
                  navigate(`/public-payment?token=${row.one_payment_token}`)
                } else {
                  navigate('/public-payment-instruksi', {
                    state: {
                      ...row,
                      isVa: false,
                    }
                  })
                }
              }
            }}
            disabled={row.expiredDate ? new Date() > new Date(row.expiredDate) : false}
            className={`d-flex align-items-center justify-content-center bg-transparent border-0 p-0 ${row.expiredDate && new Date() > new Date(row.expiredDate) ? 'text-secondary' : 'text-primary'}`}
            style={{ boxShadow: 'none' }}
          >
            <Eye size={20} />
          </CustomButton>
          <CustomButton
            size="sm"
            onClick={() => {
              setSelectedCancelId(row.id)
              setIsCancelModalOpen(true)
            }}
            className="d-flex align-items-center justify-content-center bg-transparent border-0 p-0 text-danger"
            style={{ boxShadow: 'none' }}
          >
            <Trash2 size={20} />
          </CustomButton>
        </div>
      )
    }
  ]

  const handleTotalAmount = (amount) => {
    setAmount(
      Number(
        amount?.replace?.(/\./g, '')?.replace?.(/\,/g, '.')?.slice?.(0, 15),
      ),
    )
  }

  const handleSubmit = async () => {
    try {
      setLoading(true)
      const body = {
        amount: amount,
        purpose: 'TOPUP',
        redirectToUrl: `${window.location.origin}/`,
      }
      const res = await postOnePayment(body)
      if (res?.error) {
        setErrMsg(res?.error?.data?.message ?? res?.error.message)
      } else if (res?.data?.link) {
        try {
          const url = new URL(res.data.link)
          url.searchParams.delete('havePendingTopupUniqueCode')
          navigate(`${url.pathname}${url.search}`, {
            replace: true,
            state: {
              havePendingTopupUniqueCode: res.data.havePendingTopupUniqueCode,
              amount: amount
            }
          })
        } catch {
          // Invalid URL format
        }
      }
    } catch (error) {
      setErrMsg(error?.response?.data?.message ?? error.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    setErrMsg('')
  }, [amount])

  useEffect(() => {
    if (pendingBcaList?.length === 0 && activeTab === '2') {
      setActiveTab('1')
    }
  }, [pendingBcaList?.length, activeTab])

  useEffect(() => {
    if (location.state?.fromInstruction) return;

    if (pendingOnePaymentData && pendingOnePaymentData.link) {
      try {
        const url = new URL(pendingOnePaymentData.link)
        navigate(`${url.pathname}${url.search}`, { replace: true })
      } catch {
        // Invalid URL format
      }
    }
  }, [pendingOnePaymentData, navigate, location.state])

  return (
    <Container className="custom-container pb-5">
      <div className="d-flex align-items-start gap-2 pt-3 mb-4">
        <BreadCrumbs breadCrumbActive={'Tambah uang belanja'} />
      </div>
      <div className="d-flex justify-content-center">
        <Card
          body
          className="w-100 p-4"
          style={{
            maxWidth: '800px',
            borderRadius: '20px',
            border: 'none',
            boxShadow: '0 10px 40px rgba(0,0,0,0.05)',
            transition: 'all 0.3s ease'
          }}
        >
          <h4 className="fw-bold mb-4 text-dark" style={{ letterSpacing: '-0.5px' }}>
            Tambah uang belanja
          </h4>
          <Nav tabs className="mb-4 d-flex justify-content-center" style={{ borderBottom: '2px solid #f1f5f9' }}>
            <NavItem>
              <NavLink
                className={`fw-bold ${activeTab === '1' ? 'text-primary border-primary' : 'text-secondary'}`}
                style={{
                  cursor: 'pointer',
                  border: 'none',
                  borderBottom: activeTab === '1' ? '2px solid' : '2px solid transparent',
                  backgroundColor: 'transparent',
                  padding: '10px 20px',
                }}
                onClick={() => setActiveTab('1')}
              >
                Topup Baru
              </NavLink>
            </NavItem>
            {pendingBcaList?.length > 0 && (
              <NavItem>
                <NavLink
                  className={`fw-bold d-flex align-items-center gap-2 ${activeTab === '2' ? 'text-primary border-primary' : 'text-secondary'}`}
                  style={{
                    cursor: 'pointer',
                    border: 'none',
                    borderBottom: activeTab === '2' ? '2px solid' : '2px solid transparent',
                    backgroundColor: 'transparent',
                    padding: '10px 20px',
                  }}
                  onClick={() => setActiveTab('2')}
                >
                  Menunggu Pembayaran
                  <div className="bg-danger text-white rounded-circle flex-centered" style={{ width: '20px', height: '20px', fontSize: '11px' }}>
                    {pendingBcaList.length}
                  </div>
                </NavLink>
              </NavItem>
            )}
          </Nav>

          <TabContent activeTab={activeTab}>
            <TabPane tabId="1">
              <FormGroup className="mb-4 mt-2">
                <Label className="fw-600 text-secondary mb-2" style={{ fontSize: '14px' }}>Nominal Topup</Label>
                <InputGroup
                  className="rounded-3 overflow-hidden shadow-sm"
                  style={{ border: '1px solid #e2e8f0' }}
                >
                  <InputGroupText className="bg-light border-0 fw-bold text-dark px-4" style={{ fontSize: '18px' }}>
                    Rp
                  </InputGroupText>
                  <AmountInput
                    placeholder="0"
                    name="amountInput"
                    id="amountInput"
                    max={100000000000}
                    cb={handleTotalAmount}
                    className="form-control border-0 px-3 py-3 fw-bold text-dark"
                    style={{ fontSize: '24px', boxShadow: 'none' }}
                    disabled={loading || loadingPending}
                  />
                </InputGroup>
              </FormGroup>

              {amount && amount < 10000 && amount !== 0 ? (
                <FormGroup>
                  <div className="text-danger d-flex align-items-center gap-2 p-2 rounded" style={{ backgroundColor: '#fff5f5', fontSize: '13px' }}>
                    <AlertCircle size={16} /> Minimum topup sebesar Rp 10.000
                  </div>
                </FormGroup>
              ) : null}

              {errMsg ? (
                <FormGroup>
                  <div className="text-danger d-flex align-items-center gap-2 p-2 rounded" style={{ backgroundColor: '#fff5f5', fontSize: '13px' }}>
                    <AlertCircle size={16} /> {errMsg}
                  </div>
                </FormGroup>
              ) : null}

              <div className="mt-4">
                <CustomButton
                  disabled={!amount || loading || amount < 10000 || !accessTopup}
                  onClick={() => handleSubmit()}
                  color="primary"
                  block
                  className="py-3 fw-bold shadow-sm"
                  style={{
                    borderRadius: '12px',
                    fontSize: '16px',
                  }}
                >
                  Lanjutkan Pembayaran
                </CustomButton>
              </div>
            </TabPane>

            <TabPane tabId="2">
              <div className="mt-4">
                {loadingPending ? (
                  <div className="d-flex justify-content-center p-5">
                    <Spinner color="primary" />
                  </div>
                ) : (
                  <DataTable
                    columns={columns}
                    data={pendingBcaList}
                    noHeader
                    pagination={false}
                  />
                )}
              </div>
            </TabPane>
          </TabContent>
        </Card>
      </div>

      <Modal
        centered
        size="sm"
        isOpen={isCancelModalOpen}
        toggle={() => setIsCancelModalOpen(!isCancelModalOpen)}
      >
        <ModalBody className="d-flex flex-column">
          <p className="fw-semibold">Batalkan Topup</p>
          <p className="small">
            Anda yakin untuk membatalkan transaksi topup ini?
          </p>
          <div className="d-flex gap-2">
            <CustomButton
              block
              outline
              disabled={loading}
              color="primary"
              onClick={() => setIsCancelModalOpen(false)}
            >
              Tutup
            </CustomButton>
            <CustomButton
              disabled={loading}
              block
              color="primary"
              onClick={() => {
                if (selectedCancelId) {
                  handleCancelTopup(selectedCancelId)
                }
              }}
            >
              Ya, Batalkan
            </CustomButton>
          </div>
        </ModalBody>
      </Modal>
    </Container>
  )
}

export default TopupOnePayment
